
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { truckingBillAPI, waybillAPI, collectionAPI } from '../../api/waybillAPI';
import useGuardedMutation from '../../hooks/useGuardedMutation';

function GLStatusBadge({ status, error, collectionId, onRetry, onVoid, isVoided, loading, disabled }) {
  if (isVoided) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-600">
        VOID
      </span>
    );
  }
  if (status === 'posted') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Posted</span>;
  }
  if (status === 'failed') {
    return (
      <div className="flex items-center gap-1">
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700" title={error || 'GL posting failed'}>
          Failed
        </span>
        <button onClick={() => onRetry(collectionId)}
          disabled={disabled || loading}
          className="text-xs text-teal-600 hover:underline whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed">
          {loading ? 'Posting…' : 'Retry'}
        </button>
      </div>
    );
  }
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">Pending</span>;
}
import AuditTimeline from '../../components/billing/AuditTimeline';
import { NoSymbolIcon, PlusCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import systemSettingsAPI from '../../api/systemSettingsAPI';
  
const PHP = (n) => new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const today = () => new Date().toISOString().split('T')[0];

const emptyForm = {
  BillName: '', BillDate: today(), BillDueDate: '',
  paymentTerm: '',
  WayBillId: '', printFreight: true, HasWithHolding: false,
  IsReceived: false, StrippingFee: 0, BillTypeId: 1,
  referenceNumber: '', notes: '', waybillDescription: '',
  additionalStrippingFee: 0,
};

// ─── Reusable Collections Panel ──────────────────────────────────────────────
function CollectionsPanel({
  id, amountDue, isVoided, form, collections, setCollections, collectionsLoading,
  showPaymentForm, setShowPaymentForm, paymentForm, setPaymentForm,
  setForm, setIsVoided, setError,
}) {
  const totalCollected = collections.reduce((s, c) => c.isVoided ? s : s + c.amount, 0);
  const outstanding    = Math.max((amountDue || 0) - totalCollected, 0);
  // Guards the retry/void GL actions below — one collectionId in flight at a time,
  // mirrors the waybill GL retry button's `glPosting` guard (WaybillEditPage.jsx).
  const [glActionPendingId, setGlActionPendingId] = useState(null);

  const { mutate: handleAddPayment, isPending: paymentSaving } = useGuardedMutation(async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) return;
    try {
      const isDeduction = paymentForm.transactionType === 'deduction';
      const payload = {
        billId:          id,
        amount:          parseFloat(paymentForm.amount),
        paymentDate:     paymentForm.paymentDate,
        transactionType: paymentForm.transactionType,
        notes:           paymentForm.notes,
      };
      if (isDeduction) {
        payload.deductionType = paymentForm.deductionType;
      } else {
        payload.paymentMethod  = paymentForm.paymentMethod;
        payload.referenceNo    = paymentForm.referenceNo;
        if (paymentForm.paymentMethod === '2307') {
          payload.withheldAmount = parseFloat(paymentForm.withheldAmount) || 0;
        }
      }
      const res = await collectionAPI.addPayment(payload);
      const { collection, bill: updatedBill } = res.data.data;
      setCollections((prev) => [collection, ...prev]);
      if (updatedBill) {
        setForm((prev) => ({
          ...prev,
          status:         updatedBill.status,
          IsReceived:     updatedBill.IsReceived,
          totalCollected: updatedBill.totalCollected,
          outstanding:    updatedBill.outstanding,
        }));
        if (updatedBill.isVoided === false) setIsVoided(false);
      }
      setPaymentForm({ transactionType: 'payment', deductionType: 'discount', withheldAmount: '', amount: '', paymentDate: new Date().toISOString().split('T')[0],
                       paymentMethod: 'cash', referenceNo: '', notes: '' });
      setShowPaymentForm(false);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save payment.');
      throw e; // let useGuardedMutation know this attempt failed — keeps the idempotency key for retry
    }
  });

  const handleDeletePayment = async (collectionId) => {
    if (!window.confirm('Delete this payment record? This will update the bill balance.')) return;
    try {
      const res = await collectionAPI.deletePayment(collectionId);
      setCollections((prev) => prev.filter((c) => c._id !== collectionId));
      if (res.data?.data?.bill) {
        const b = res.data.data.bill;
        setForm((prev) => ({
          ...prev, status: b.status, IsReceived: b.IsReceived,
          totalCollected: b.totalCollected, outstanding: b.outstanding,
        }));
      }
    } catch {
      setError('Failed to delete payment.');
    }
  };

  const handleRetryGL = async (collectionId) => {
    if (glActionPendingId) return;
    setGlActionPendingId(collectionId);
    try {
      const res = await collectionAPI.retryGLPost(collectionId);
      const updated = res.data?.data;
      if (updated) setCollections((prev) => prev.map((c) => c._id === collectionId ? { ...c, ...updated } : c));
    } catch (e) {
      const updated = e?.response?.data?.data;
      if (updated) setCollections((prev) => prev.map((c) => c._id === collectionId ? { ...c, ...updated } : c));
      setError(e?.response?.data?.message || 'GL retry failed.');
    } finally {
      setGlActionPendingId(null);
    }
  };

  const handleVoidPayment = async (collectionId) => {
    if (glActionPendingId) return;
    if (!window.confirm('Void this payment? A reversing GL entry will be created. This cannot be undone.')) return;
    setGlActionPendingId(collectionId);
    try {
      const res = await collectionAPI.voidCollection(collectionId);
      const { collection: updated, bill: updatedBill } = res.data?.data || {};
      if (updated) setCollections((prev) => prev.map((c) => c._id === collectionId ? { ...c, ...updated } : c));
      if (updatedBill) {
        setForm((prev) => ({
          ...prev,
          status:         updatedBill.status,
          IsReceived:     updatedBill.IsReceived,
          totalCollected: updatedBill.totalCollected,
          outstanding:    updatedBill.outstanding,
        }));
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to void payment.');
    } finally {
      setGlActionPendingId(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Balance summary card */}
      <div className="bg-white rounded-lg shadow p-4 grid grid-cols-3 gap-4 text-center">
        <div>
          <p className="text-xs text-gray-400 uppercase mb-1">Amount Due</p>
          <p className="text-lg font-semibold text-gray-900">₱{PHP(amountDue)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 uppercase mb-1">Total Collected</p>
          <p className="text-lg font-semibold text-green-600">₱{PHP(totalCollected)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 uppercase mb-1">Outstanding</p>
          <p className={`text-lg font-semibold ${outstanding <= 0 ? 'text-green-600' : 'text-red-600'}`}>
            ₱{PHP(outstanding)}
          </p>
        </div>
      </div>

      {/* Add Payment button */}
      {!isVoided && !form.isVoided && outstanding > 0 && (
        <button
          onClick={() => setShowPaymentForm((p) => !p)}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700"
        >
          <PlusCircleIcon className="h-4 w-4" />
          {showPaymentForm ? 'Cancel' : 'Add Payment'}
        </button>
      )}

      {/* Payment form */}
      {showPaymentForm && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 space-y-3">
          {/* Transaction type toggle */}
          <div className="flex gap-2">
            {[{ val: 'payment', label: 'Record Payment' }, { val: 'deduction', label: 'Apply Deduction' }].map(({ val, label }) => (
              <button key={val} type="button"
                onClick={() => setPaymentForm((p) => ({ ...p, transactionType: val }))}
                className={`px-3 py-1.5 text-sm rounded-lg font-medium border transition-colors ${
                  paymentForm.transactionType === val
                    ? val === 'payment' ? 'bg-teal-600 text-white border-teal-600' : 'bg-orange-500 text-white border-orange-500'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}>
                {label}
              </button>
            ))}
          </div>

          <h3 className={`text-sm font-semibold ${paymentForm.transactionType === 'deduction' ? 'text-orange-700' : 'text-teal-800'}`}>
            {paymentForm.transactionType === 'deduction' ? 'Apply Deduction / Adjustment' : 'Record Payment'}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Amount (₱) *</label>
              <input type="number" step="0.01" min="0.01"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm((p) => ({ ...p, amount: e.target.value }))}
                placeholder="0.00"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <div className="flex gap-2 mt-1">
                <button
                  onClick={() => setPaymentForm((p) => ({ ...p, amount: outstanding.toFixed(2) }))}
                  className="text-xs text-teal-600 hover:underline"
                >
                  Full (₱{PHP(outstanding)})
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Date *</label>
              <input type="date"
                value={paymentForm.paymentDate}
                onChange={(e) => setPaymentForm((p) => ({ ...p, paymentDate: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {paymentForm.transactionType === 'deduction' ? (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Deduction Type</label>
                <select value={paymentForm.deductionType}
                  onChange={(e) => setPaymentForm((p) => ({ ...p, deductionType: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
                  <option value="discount">Discount</option>
                  <option value="damage">Damage</option>
                  <option value="missing">Missing</option>
                  <option value="general_expense">General Expense</option>
                  <option value="2307">2307 (Withholding Tax)</option>
                  <option value="other">Other</option>
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Payment Method</label>
                <select
                  value={paymentForm.paymentMethod}
                  onChange={(e) => setPaymentForm((p) => ({ ...p, paymentMethod: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="gcash">GCash</option>
                  <option value="other">Other</option>
                </select>
              </div>
            )}

            {paymentForm.transactionType === 'payment' && paymentForm.paymentMethod === '2307' && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Withheld Amount (₱)</label>
                <input type="number" step="0.01" min="0"
                  value={paymentForm.withheldAmount}
                  onChange={(e) => setPaymentForm((p) => ({ ...p, withheldAmount: e.target.value }))}
                  placeholder="0.00"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            )}

            {paymentForm.transactionType === 'payment' && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Reference No.</label>
                <input type="text"
                  value={paymentForm.referenceNo}
                  onChange={(e) => setPaymentForm((p) => ({ ...p, referenceNo: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            )}

            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
              <input type="text"
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm((p) => ({ ...p, notes: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button onClick={() => setShowPaymentForm(false)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button
              onClick={handleAddPayment}
              disabled={paymentSaving || !paymentForm.amount}
              className={`px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 ${
                paymentForm.transactionType === 'deduction' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-teal-600 hover:bg-teal-700'
              }`}
            >
              {paymentSaving ? 'Saving…' : paymentForm.transactionType === 'deduction' ? 'Apply Deduction' : 'Save Payment'}
            </button>
          </div>
        </div>
      )}

      {/* Collections list */}
      {collectionsLoading && <div className="text-center text-gray-400 py-8 text-sm">Loading…</div>}
      {!collectionsLoading && collections.length === 0 && (
        <div className="text-center text-gray-400 py-8 text-sm">No payments recorded yet.</div>
      )}
      {!collectionsLoading && collections.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Date', 'Amount', 'Type', 'Reference No.', 'Notes', 'Received By', 'GL', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {collections.map((c) => {
                const isDeduction = c.transactionType === 'deduction';
                const deductionLabels = { discount: 'Discount', damage: 'Damage', missing: 'Missing', general_expense: 'Gen. Expense' };
                const typeLabel = isDeduction
                  ? (deductionLabels[c.deductionType] || c.deductionType)
                  : (c.paymentMethod === '2307' ? '2307' : (c.paymentMethod?.replace(/_/g, ' ') || ''));
                return (
                  <tr key={c._id} className={`hover:bg-gray-50 ${c.isVoided ? 'opacity-60' : ''}`}>
                    <td className="px-4 py-3 whitespace-nowrap">{new Date(c.paymentDate).toLocaleDateString('en-PH')}</td>
                    <td className={`px-4 py-3 font-medium ${c.isVoided ? 'line-through text-gray-400' : isDeduction ? 'text-orange-600' : 'text-green-700'}`}>
                      ₱{PHP(c.amount)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                        isDeduction ? 'bg-orange-100 text-orange-700' : 'bg-teal-100 text-teal-700'
                      }`}>{typeLabel}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{c.referenceNo || '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{c.notes || '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{c.receivedByName || '—'}</td>
                    <td className="px-4 py-3">
                      <GLStatusBadge
                        status={c.glPostingStatus}
                        error={c.glPostingError}
                        collectionId={c._id}
                        isVoided={c.isVoided}
                        onRetry={handleRetryGL}
                        onVoid={handleVoidPayment}
                        loading={glActionPendingId === c._id}
                        disabled={!!glActionPendingId}
                      />
                    </td>
                    <td className="px-4 py-3">
                      {!c.isVoided && c.glPostingStatus === 'posted' && (
                        <button onClick={() => handleVoidPayment(c._id)}
                          disabled={!!glActionPendingId}
                          className="px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Void this payment and create a reversing GL entry">
                          {glActionPendingId === c._id ? 'Voiding…' : 'Void'}
                        </button>
                      )}
                      {!c.isVoided && c.glPostingStatus !== 'posted' && (
                        <button onClick={() => handleDeletePayment(c._id)}
                          disabled={!!glActionPendingId}
                          className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-40 disabled:cursor-not-allowed" title="Delete payment">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function FFBillingEditPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isAdd = !id;
  const navigate = useNavigate();

  const [form, setForm] = useState({ ...emptyForm, WayBillId: searchParams.get('waybillId') || '' });
  const [lines, setLines] = useState([]);
  const [waybills, setWaybills] = useState([]);
  const [selectedWaybill, setSelectedWaybill] = useState(null);
  const [loading, setLoading] = useState(!isAdd);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFetched, setAuditFetched] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [isVoided, setIsVoided] = useState(false);
  const [voidedInfo, setVoidedInfo] = useState({ at: null, reason: '' });
  const [markingSent, setMarkingSent] = useState(false);
  const [sentSuccess, setSentSuccess] = useState(false);

  // ── Collections tab state ──────────────────────────────────────────────
  const [collections, setCollections]             = useState([]);
  const [collectionsFetched, setCollectionsFetched] = useState(false);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [showPaymentForm, setShowPaymentForm]     = useState(false);
  const [paymentForm, setPaymentForm]             = useState({
    transactionType: 'payment', deductionType: 'discount', withheldAmount: '',
    amount: '', paymentDate: today(), paymentMethod: 'cash', referenceNo: '', notes: '',
  });

  const [minAmount, setMinAmount] = useState(250);
  useEffect(() => {
    if (isAdd) {
      systemSettingsAPI.get()
        .then(r => setMinAmount(r.data?.data?.minimumBillingAmount ?? 250))
        .catch(() => {});
    }
  }, [isAdd]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'audit' && !auditFetched && id) {
      setAuditLoading(true);
      truckingBillAPI.getAuditLog(id)
        .then((res) => {
          setAuditLog(Array.isArray(res.data?.data) ? res.data.data : []);
          setAuditFetched(true);
        })
        .catch(() => {})
        .finally(() => setAuditLoading(false));
    }
    if (tab === 'collections' && !collectionsFetched && id) {
      setCollectionsLoading(true);
      collectionAPI.getByBill(id)
        .then((res) => {
          setCollections(Array.isArray(res.data?.data) ? res.data.data : []);
          setCollectionsFetched(true);
        })
        .catch(() => {})
        .finally(() => setCollectionsLoading(false));
    }
  };

  useEffect(() => {
    const prefilledWaybillId = searchParams.get('waybillId') || '';

    if (isAdd) {
      truckingBillAPI.nextNumber(1).then((r) => setForm((p) => ({ ...p, BillName: r.data.number }))).catch(() => {});
    }

    waybillAPI.getAll({ shipmentType: 'freightforwarding' })
      .then((r) => {
        const wbs = r.data || [];
        setWaybills(wbs);
        // Pre-select waybill and load its lines in add mode
        if (isAdd && prefilledWaybillId) {
          const wb = wbs.find((w) => w._id === prefilledWaybillId);
          if (wb) setSelectedWaybill(wb);
          waybillAPI.getLines(prefilledWaybillId)
            .then((lr) => { const d = lr.data?.data; setLines(Array.isArray(d) ? d : []); })
            .catch(() => {});
        }
      }).catch(() => {});

    if (!isAdd) {
      truckingBillAPI.getById(id).then((r) => {
        const { bill, lines: l } = r.data;
        setForm({
          BillName: bill.BillName || '',
          BillDate: bill.BillDate ? bill.BillDate.split('T')[0] : today(),
          BillDueDate: bill.BillDueDate ? bill.BillDueDate.split('T')[0] : '',
          paymentTerm: bill.paymentTerm || '',
          WayBillId: bill.WayBillId?._id || bill.WayBillId || '',
          printFreight: bill.printFreight !== false,
          HasWithHolding: !!bill.HasWithHolding,
          IsReceived: !!bill.IsReceived,
          StrippingFee: bill.StrippingFee || 0,
          BillTypeId: 1,
          referenceNumber: bill.referenceNumber || '',
          notes: bill.notes || '',
          waybillDescription: bill.waybillDescription || '',
          additionalStrippingFee: bill.additionalStrippingFee || 0,
          status: bill.status || 'draft',
        });
        setIsVoided(!!bill.isVoided);
        if (bill.isVoided) setVoidedInfo({ at: bill.voidedAt, reason: bill.voidReason || '' });
        setMinAmount(bill.minimumBillingAmount ?? 250);
        if (bill.WayBillId) {
          const wb = bill.WayBillId;
          // WayBillId is populated; CustomerId inside it is now sub-populated with 'name'
          setSelectedWaybill(typeof wb === 'object' ? wb : { _id: wb });
        }
        setLines(Array.isArray(l) ? l : []);
        setLoading(false);
      }).catch(() => { setError('Failed to load bill.'); setLoading(false); });
    }
  }, [id, isAdd]);

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    if (field === 'WayBillId') {
      const wayBillId = e.target.value;
      const wb = waybills.find((w) => w._id === wayBillId);
      setSelectedWaybill(wb || null);
      const wbDate = wb?.TransactionDate || wb?.transactionDate;
      if (form.paymentTerm && wbDate) {
        const due = computeDueDate(wbDate, form.paymentTerm);
        if (due) {
          setForm((p) => ({ ...p, WayBillId: wayBillId, BillDueDate: due }));
          if (wayBillId) {
            waybillAPI.getLines(wayBillId)
              .then((r) => { const d = r.data?.data; setLines(Array.isArray(d) ? d : []); })
              .catch(() => {});
          } else {
            setLines([]);
          }
          return;
        }
      }
      setForm((p) => ({ ...p, [field]: val }));
      // Copy waybill lines into billing lines
      if (wayBillId) {
        waybillAPI.getLines(wayBillId)
          .then((r) => { const d = r.data?.data; setLines(Array.isArray(d) ? d : []); })
          .catch(() => {});
      } else {
        setLines([]);
      }
      return;
    }
    setForm((p) => ({ ...p, [field]: val }));
  };

  // Computed totals
  const safeLines = Array.isArray(lines) ? lines : [];
  const sumSubTotal = safeLines.reduce((s, l) => s + (l.SubTotal || 0), 0);
  const subtotal = Math.max(sumSubTotal, minAmount);
  const totalAmount = subtotal;
  const netOfVat = totalAmount / 1.12;
  const vat = netOfVat * 0.12;
  const withholding = form.HasWithHolding ? netOfVat * 0.02 : 0;
  const stripping = parseFloat(form.StrippingFee) || 0;
  const addlStripping = parseFloat(form.additionalStrippingFee) || 0;
  const totalFees = stripping + addlStripping;
  const amountDue = form.HasWithHolding
    ? totalAmount + totalFees - withholding
    : totalAmount + totalFees;
  const totalQty = safeLines.reduce((s, l) => s + (l.Quantity || 0), 0);
  const waybillDate = selectedWaybill?.TransactionDate || selectedWaybill?.transactionDate || '';

  function computeDueDate(waybillDateValue, term) {
    if (!waybillDateValue || !term) return '';
    const base = new Date(waybillDateValue);
    if (isNaN(base.getTime())) return '';
    const days = { immediate: 0, net15: 15, net30: 30, net45: 45 };
    const offset = days[term];
    if (offset === undefined) return '';
    const due = new Date(base);
    due.setDate(due.getDate() + offset);
    return due.toISOString().split('T')[0];
  }

  const { mutate: handleSave, isPending: saving } = useGuardedMutation(async () => {
    if (isVoided) return;
    setError('');
    try {
      const payload = { ...form, lines };
      if (isAdd) {
        const r = await truckingBillAPI.create(payload);
        navigate(`/billing/ff-billing/${r.data.bill._id}/edit`);
      } else {
        await truckingBillAPI.update(id, payload);
      }
    } catch (e) {
      const d = e?.response?.data;
      setError(d?.detail ? `${d.error}: ${d.detail}` : (d?.error || 'Failed to save.'));
      throw e; // let useGuardedMutation know this attempt failed — keeps the idempotency key for retry
    }
  });

  const handleVoid = async () => {
    if (!voidReason.trim()) return;
    setVoiding(true);
    try {
      await truckingBillAPI.voidBill(id, voidReason.trim());
      setIsVoided(true);
      setVoidedInfo({ at: new Date().toISOString(), reason: voidReason.trim() });
      setForm((p) => ({ ...p, status: 'voided' }));
      setShowVoidModal(false);
      setAuditFetched(false);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to void bill.');
    } finally {
      setVoiding(false);
    }
  };

  const handleMarkSent = async () => {
    setMarkingSent(true);
    setSentSuccess(false);
    try {
      await truckingBillAPI.updateStatus(id, 'sent');
      setForm((p) => ({ ...p, status: 'sent' }));
      setSentSuccess(true);
      if (auditFetched) {
        truckingBillAPI.getAuditLog(id)
          .then((res) => setAuditLog(Array.isArray(res.data?.data) ? res.data.data : []))
          .catch(() => {});
      }
      setTimeout(() => setSentSuccess(false), 3000);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to mark as sent.');
    } finally {
      setMarkingSent(false);
    }
  };

  if (loading) return <Layout pageTitle="FF Billing"><div className="flex justify-center items-center h-48 text-gray-400">Loading…</div></Layout>;

  return (
    <Layout pageTitle={isAdd ? 'New FF Bill' : `FF Bill — ${form.BillName}`}>
      <div className="space-y-6 max-w-4xl">
        {/* Tab bar */}
        {!isAdd && (
          <div className="border-b border-gray-200">
            <nav className="flex gap-0" aria-label="Tabs">
              {[
                { key: 'details',     label: 'Details' },
                { key: 'audit',       label: 'Audit Trail' },
                { key: 'collections', label: 'Collections' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-teal-600 text-teal-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* AUDIT TRAIL tab */}
        {!isAdd && activeTab === 'audit' && (
          <div className="space-y-6">
            {auditLoading && (
              <div className="text-center text-gray-400 py-12 text-sm">Loading audit trail…</div>
            )}
            {!auditLoading && auditLog.length === 0 && (
              <div className="text-center text-gray-400 py-12 text-sm">No audit records found.</div>
            )}
            {!auditLoading && auditLog.length > 0 && (
              <AuditTimeline entries={auditLog} />
            )}
          </div>
        )}

        {/* COLLECTIONS tab */}
        {!isAdd && activeTab === 'collections' && (
          <CollectionsPanel
            id={id}
            amountDue={amountDue}
            isVoided={isVoided}
            form={form}
            collections={collections}
            setCollections={setCollections}
            collectionsLoading={collectionsLoading}
            showPaymentForm={showPaymentForm}
            setShowPaymentForm={setShowPaymentForm}
            paymentForm={paymentForm}
            setPaymentForm={setPaymentForm}
            setForm={setForm}
            setIsVoided={setIsVoided}
            setError={setError}
          />
        )}

        {/* DETAILS tab */}
        {(isAdd || activeTab === 'details') && (
          <>
        {/* Voided banner */}
        {!isAdd && isVoided && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-300 text-red-700 rounded-lg px-4 py-3 text-sm">
            <NoSymbolIcon className="h-5 w-5 shrink-0" />
            <div>
              <span className="font-semibold">This bill has been voided.</span>
              {voidedInfo.reason && <span className="ml-2">Reason: {voidedInfo.reason}</span>}
              {voidedInfo.at && <span className="ml-2 text-red-500">{new Date(voidedInfo.at).toLocaleDateString('en-PH', { dateStyle: 'medium' })}</span>}
            </div>
          </div>
        )}

        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>}

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Bill Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <F label="Bill No." value={form.BillName} onChange={set('BillName')} />
            <F label="Bill Date" type="date" value={form.BillDate} onChange={set('BillDate')} />
            <F label="Due Date" type="date" value={form.BillDueDate} onChange={set('BillDueDate')} />
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Payment Terms</label>
              <select
                value={form.paymentTerm}
                onChange={(e) => {
                  const term = e.target.value;
                  const due = computeDueDate(waybillDate, term);
                  setForm((p) => ({
                    ...p,
                    paymentTerm: term,
                    BillDueDate: due || p.BillDueDate,
                  }));
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">— Select Terms —</option>
                <option value="immediate">Due Immediately</option>
                <option value="net15">Net 15 days</option>
                <option value="net30">Net 30 days</option>
                <option value="net45">Net 45 days</option>
              </select>
              {form.paymentTerm && waybillDate && (
                <p className="text-xs text-gray-400 mt-1">
                  Based on waybill date:{' '}
                  {new Date(waybillDate).toLocaleDateString('en-PH', {
                    month: 'short', day: 'numeric', year: 'numeric'
                  })}
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Linked Waybill
                {form.WayBillId && (
                  <a href={`/billing/waybill/${form.WayBillId}/edit`} target="_blank" rel="noreferrer" className="ml-2 text-blue-500 hover:underline font-normal normal-case">
                    Open ↗
                  </a>
                )}
              </label>
              <select
                value={form.WayBillId}
                onChange={set('WayBillId')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">— Select Waybill —</option>
                {waybills.map((w) => (
                  <option key={w._id} value={w._id}>{w.SerialNo}</option>
                ))}
              </select>
            </div>
            <F label="Stripping Fee (₱)" type="number" value={form.StrippingFee} onChange={set('StrippingFee')} />
            <F label="Add'l Stripping Fee (₱)" type="number" value={form.additionalStrippingFee} onChange={set('additionalStrippingFee')} />
            <F label="Reference No." value={form.referenceNumber} onChange={set('referenceNumber')} />
            <div className="flex items-center gap-6 col-span-full pt-1">
              <CB label="Print Freight" checked={form.printFreight} onChange={set('printFreight')} />
              <CB label="Has Withholding" checked={form.HasWithHolding} onChange={set('HasWithHolding')} />
              <CB label="Is Received" checked={form.IsReceived} onChange={set('IsReceived')} />
            </div>
            <div className="col-span-full">
              <label className="block text-xs font-medium text-gray-500 mb-1">Waybill Description</label>
              <textarea rows={2} value={form.waybillDescription} onChange={set('waybillDescription')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div className="col-span-full">
              <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
              <textarea rows={2} value={form.notes} onChange={set('notes')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
          </div>

          {/* Auto-filled waybill info */}
          {selectedWaybill && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50 rounded-lg p-4 text-sm">
              <RO label="Customer" value={
                selectedWaybill.CustomerId?.name ||
                selectedWaybill.CustomerId?.CustomerName ||
                selectedWaybill.CustomerName ||
                selectedWaybill.customerName
              } />
              <RO label="Customer Address" value={selectedWaybill.CustomerAddress} />
              <RO label="Shipper" value={selectedWaybill.Shipper} />
              <RO label="Shipper Address" value={selectedWaybill.ShipperAddress} />
            </div>
          )}
        </div>

        {/* Line items — show when waybill selected or editing */}
        {(safeLines.length > 0 || !isAdd) && (
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Freight Lines</h2>
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Description', form.printFreight ? 'Freight' : null, 'W', 'H', 'L', 'Weight', 'Qty', 'Adj', 'SubTotal'].filter(Boolean).map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {safeLines.length === 0 ? (
                  <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-400">No line items yet.</td></tr>
                ) : (
                  safeLines.map((l, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-2">{l.Description}</td>
                      {form.printFreight && <td className="px-3 py-2">₱{PHP(l.Freight)}</td>}
                      <td className="px-3 py-2">{l.Width}</td>
                      <td className="px-3 py-2">{l.Height}</td>
                      <td className="px-3 py-2">{l.Length}</td>
                      <td className="px-3 py-2">{l.Weight}</td>
                      <td className="px-3 py-2">{l.Quantity}</td>
                      <td className="px-3 py-2">₱{PHP(l.Adjustments)}</td>
                      <td className="px-3 py-2 font-medium">{l.SubTotal > 0 ? `₱${PHP(l.SubTotal)}` : ''}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4 text-sm grid grid-cols-2 gap-2 max-w-sm ml-auto">
              <SR label="Total Qty" value={totalQty} />
              <SR label={`Subtotal (min ₱${PHP(minAmount)})`} value={`₱${PHP(subtotal)}`} />
              <SR label="Net of VAT" value={`₱${PHP(netOfVat)}`} />
              <SR label="VAT (12%)" value={`₱${PHP(vat)}`} />
              {form.HasWithHolding && <SR label="Withholding (2%)" value={`₱${PHP(withholding)}`} />}
              {stripping > 0 && <SR label="Stripping Fee" value={`₱${PHP(stripping)}`} />}
              {addlStripping > 0 && <SR label="Add'l Stripping Fee" value={`₱${PHP(addlStripping)}`} />}
              <SR label="Amount Due" value={`₱${PHP(amountDue)}`} bold />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 flex-wrap">
          <Btn primary onClick={handleSave} disabled={saving || isVoided} label={saving ? 'Saving…' : 'Save'} />
          {!isAdd && <Btn onClick={() => window.open(`/billing/ff-billing/${id}/print`, '_blank')} label="Print Billing Statement" />}
          {!isAdd && !isVoided && form.status === 'draft' && (
            <button
              onClick={handleMarkSent}
              disabled={markingSent}
              className="flex items-center gap-1.5 px-3 py-2 border border-blue-300 text-blue-600 rounded-lg text-sm hover:bg-blue-50 disabled:opacity-50"
            >
              {markingSent ? 'Updating…' : 'Mark as Sent'}
            </button>
          )}
          {!isAdd && !isVoided && form.status && form.status !== 'draft' && (
            <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
              form.status === 'sent'         ? 'bg-blue-100   text-blue-700'  :
              form.status === 'paid'         ? 'bg-green-100  text-green-700' :
              form.status === 'partially_paid' ? 'bg-yellow-100 text-yellow-700' :
              form.status === 'overdue'      ? 'bg-red-100    text-red-700'   :
              'bg-gray-100 text-gray-600'
            }`}>
              {form.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </span>
          )}
          {sentSuccess && (
            <span className="text-xs text-green-600 font-medium">✓ Marked as sent</span>
          )}
          {!isAdd && !isVoided && (
            <button
              onClick={() => setShowVoidModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 transition-colors"
            >
              <NoSymbolIcon className="h-4 w-4" />
              Void Bill
            </button>
          )}
          <Btn onClick={() => navigate('/billing/ff-billing')} label="Back" />
        </div>
          </>
        )}
      </div>

      {/* Void Modal */}
      {showVoidModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6 space-y-4">
            <div className="flex items-center gap-3">
              <NoSymbolIcon className="h-6 w-6 text-red-600" />
              <h3 className="text-base font-semibold text-gray-900">Void Bill</h3>
            </div>
            <p className="text-sm text-gray-600">This action cannot be undone. The bill will be marked as voided and saved to the audit trail.</p>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Reason for voiding <span className="text-red-500">*</span></label>
              <textarea
                rows={3}
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="Enter reason…"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => { setShowVoidModal(false); setVoidReason(''); }} className="px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">Cancel</button>
              <button
                onClick={handleVoid}
                disabled={voiding || !voidReason.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 font-medium"
              >
                {voiding ? 'Voiding…' : 'Confirm Void'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function F({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <input type={type} value={value} onChange={onChange}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
    </div>
  );
}
function CB({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input type="checkbox" checked={checked} onChange={onChange} className="h-4 w-4 text-teal-600 rounded" />
      {label}
    </label>
  );
}
function RO({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="font-medium text-gray-800">{value || '—'}</p>
    </div>
  );
}
function SR({ label, value, bold }) {
  return (
    <>
      <span className="text-gray-500">{label}</span>
      <span className={bold ? 'font-semibold text-gray-900 text-right' : 'text-gray-700 text-right'}>{value}</span>
    </>
  );
}
function Btn({ label, onClick, disabled, primary }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${primary ? 'bg-teal-600 text-white hover:bg-teal-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
      {label}
    </button>
  );
}
